import React, { useState, useMemo } from 'react';
import { Select, message } from 'antd';
import { useGetAnalysisTags } from './hooks/useGetAnalysisTags';
import { PREDEFINED_TAGS } from './predefinedTags';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  maxTags?: number;
  maxTagLength?: number;
  disabled?: boolean;
  maxUniqueTagsInSystem?: number;
}

const TagInput: React.FC<TagInputProps> = ({
  value = [],
  onChange,
  maxTags = 5,
  maxTagLength = 32,
  disabled = false,
  maxUniqueTagsInSystem = 30,
}) => {
  const [searchValue, setSearchValue] = useState('');

  // Fetch all analysis tags from backend
  const { data } = useGetAnalysisTags();

  // Merge predefined tags with backend tags
  const allAvailableTags = useMemo(() => {
    const tagSet = new Set<string>();

    // Add predefined tags first (these get priority and show immediately)
    PREDEFINED_TAGS.forEach((tag) => tagSet.add(tag));

    // Add existing tags from backend (loads after backend responds)
    if (data?.tags) {
      data.tags.forEach((tag: string) => tagSet.add(tag));
    }

    // Convert to array and sort
    return Array.from(tagSet).sort();
  }, [data?.tags]);

  // Check if we've hit the limit
  const hasReachedLimit = allAvailableTags.length >= maxUniqueTagsInSystem;

  const validateTag = (tag: string): string | null => {
    const trimmed = tag.trim();

    if (!trimmed) {
      return 'Tag cannot be empty';
    }

    // Check for invalid characters (only letters, numbers, spaces, underscores, hyphens)
    const allowedPattern = /^[A-Za-z0-9 _-]+$/;
    if (!allowedPattern.test(trimmed)) {
      return 'Only letters, numbers, spaces, underscores, and hyphens are allowed';
    }

    // Normalize for length check (lowercase, collapse spaces, replace with hyphens)
    const normalized = trimmed.toLowerCase().replace(/\s+/g, ' ').replace(/ /g, '-');

    if (normalized.length > maxTagLength) {
      return `Tag exceeds maximum length of ${maxTagLength} characters`;
    }

    return null;
  };

  const handleChange = (newValue: string[]) => {
    // Find truly new custom tags (not in existing system tags AND not already in current value)
    const newCustomTags = newValue.filter(
      (tag) => !allAvailableTags.includes(tag) && !value.includes(tag),
    );

    // Only validate limit if we're ADDING new custom tags (not removing)
    if (newCustomTags.length > 0) {
      // Account for tags already in current value that are new to the system
      const currentNewTags = value.filter((tag) => !allAvailableTags.includes(tag));

      // Calculate what the new total would be (existing system tags + current new tags + newly added tags)
      const potentialNewTotal =
        allAvailableTags.length + currentNewTags.length + newCustomTags.length;

      // Block if adding these tags would exceed the limit
      if (potentialNewTotal > maxUniqueTagsInSystem) {
        const alreadyUsed = allAvailableTags.length + currentNewTags.length;
        const allowedNewTags = maxUniqueTagsInSystem - alreadyUsed;
        message.warning(
          allowedNewTags > 0
            ? `Can only add ${allowedNewTags} more unique tag${allowedNewTags === 1 ? '' : 's'}. System limit: ${maxUniqueTagsInSystem}`
            : `Maximum ${maxUniqueTagsInSystem} unique tags reached. Please select from existing tags.`,
        );
        return;
      }

      // Validate each new tag
      for (const tag of newCustomTags) {
        const error = validateTag(tag);
        if (error) {
          message.error(error);
          return;
        }
      }
    }

    onChange(newValue);
    setSearchValue('');
  };

  const handleSearch = (searchText: string) => {
    // Limit search input length to maxTagLength
    if (searchText.length <= maxTagLength) {
      setSearchValue(searchText);
    }
  };

  // Convert available tags to Select options
  const options = allAvailableTags.map((tag) => ({
    label: tag,
    value: tag,
  }));

  // Dynamic placeholder based on limit status
  const placeholder = hasReachedLimit
    ? `Select from ${allAvailableTags.length} existing tags (limit reached)`
    : `Select tags or create new (${allAvailableTags.length}/${maxUniqueTagsInSystem})`;

  return (
    <div>
      <Select
        mode="tags"
        style={{ width: '100%' }}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onSearch={handleSearch}
        searchValue={searchValue}
        options={options}
        maxCount={maxTags}
        disabled={disabled}
        tokenSeparators={[',']}
        dropdownRender={(menu) => (
          <div>
            {menu}
            {hasReachedLimit && (
              <div
                style={{
                  padding: '8px 12px',
                  color: '#ff4d4f',
                  fontSize: 12,
                  borderTop: '1px solid #f0f0f0',
                  backgroundColor: '#fff7e6',
                }}
              >
                ⚠️ Tag limit reached ({allAvailableTags.length}/{maxUniqueTagsInSystem}). Select
                from existing tags only.
              </div>
            )}
            {!hasReachedLimit && allAvailableTags.length > 20 && (
              <div
                style={{
                  padding: '8px 12px',
                  color: '#8c8c8c',
                  fontSize: 12,
                  borderTop: '1px solid #f0f0f0',
                }}
              >
                💡 {allAvailableTags.length}/{maxUniqueTagsInSystem} tags used. Consider reusing
                existing tags.
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default TagInput;
